import { apiFetch } from "../apiFetch";
import { getApiBase } from "../config";

const apiBase = getApiBase();

export interface PaginationMeta {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

export interface RunListItem {
  id: string;
  user_id: string;
  machine_id: string;
  run_type: string;
  job_type: string;
  input_path: string;
  output_root: string;
  files_total: number;
  files_ok: number;
  files_failed: number;
  files_no_match: number;
  pages_billed: number;
  status: string;
  started_at: string | null;
  ended_at: string | null;
  edge_version: string | null;
  org_id?: string;
}

export interface PeriodStats {
  runs: number;
  files_processed: number;
  files_ok: number;
  files_failed: number;
  pages_billed: number;
  tokens_consumed: number;
  failure_rate: number;
}

export interface SummaryResponse {
  org_id?: string;
  last_24h: PeriodStats;
  last_7d: PeriodStats;
  last_30d: PeriodStats;
  all_time: PeriodStats;
}

export interface RunListResponse {
  runs: RunListItem[];
  pagination: PaginationMeta;
}

export interface RunDetailResponse extends RunListItem {
  pages_reserved: number;
  tokens_reserved: number;
  config_snapshot: any;
  file_status_breakdown: {
    ok: number;
    no_match: number;
    failed: number;
  };
}

export interface ProcessedFileItem {
  id: string;
  source_filename: string;
  source_rel_path: string | null;
  source_sha256: string;
  fingerprint_hash: string | null;
  template_id: string | null;
  job_type: string;
  original_pages: number;
  pages_redact: number;
  pages_split: number;
  pages_billed: number;
  status: string;
  failure_reason: string | null;
  created_at: string | null;
}

export interface ProcessedFileListResponse {
  files: ProcessedFileItem[];
  pagination: PaginationMeta;
}

// Org-scoped endpoints
export const reportsApi = {
  summary: async (): Promise<SummaryResponse> => {
    return apiFetch(`${apiBase}/reports/summary`);
  },

  runs: async (params: {
    page?: number;
    per_page?: number;
    status?: string;
    job_type?: string;
  } = {}): Promise<RunListResponse> => {
    const qs = new URLSearchParams();
    if (params.page) qs.set("page", String(params.page));
    if (params.per_page) qs.set("per_page", String(params.per_page));
    if (params.status) qs.set("status", params.status);
    if (params.job_type) qs.set("job_type", params.job_type);
    const q = qs.toString();
    return apiFetch(`${apiBase}/reports/runs${q ? `?${q}` : ""}`);
  },

  runDetail: async (runId: string): Promise<RunDetailResponse> => {
    return apiFetch(`${apiBase}/reports/runs/${runId}`);
  },

  runFiles: async (runId: string, params: {
    page?: number;
    per_page?: number;
    status?: string;
  } = {}): Promise<ProcessedFileListResponse> => {
    const qs = new URLSearchParams();
    if (params.page) qs.set("page", String(params.page));
    if (params.per_page) qs.set("per_page", String(params.per_page));
    if (params.status) qs.set("status", params.status);
    const q = qs.toString();
    return apiFetch(`${apiBase}/reports/runs/${runId}/files${q ? `?${q}` : ""}`);
  },
};

// Admin endpoints (SUPERADMIN)
export const adminReportsApi = {
  summary: async (): Promise<SummaryResponse> => {
    return apiFetch(`${apiBase}/admin/reports/summary`);
  },

  runs: async (params: {
    page?: number;
    per_page?: number;
    org_id?: string;
    status?: string;
    job_type?: string;
  } = {}): Promise<RunListResponse> => {
    const qs = new URLSearchParams();
    if (params.page) qs.set("page", String(params.page));
    if (params.per_page) qs.set("per_page", String(params.per_page));
    if (params.org_id) qs.set("org_id", params.org_id);
    if (params.status) qs.set("status", params.status);
    if (params.job_type) qs.set("job_type", params.job_type);
    const q = qs.toString();
    return apiFetch(`${apiBase}/admin/reports/runs${q ? `?${q}` : ""}`);
  },
};
